import { useTranslation } from "react-i18next"
import { Link, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { Check, X, Loader2, AlertCircle } from "lucide-react"

interface PlanFeature {
    label: string
    enabled: boolean
}

interface Plan {
    id: string
    slug: string
    name: string
    description: string
    price: number
    price_formatted: string
    currency: string
    interval: string
    is_featured: boolean
    trial_days: number
    features: Record<string, PlanFeature>
    quotas: Record<string, number>
}

async function fetchPublicPlans(): Promise<Plan[]> {
    const response = await fetch('/api/v1/subscriptions/plans')
    if (!response.ok) throw new Error('Failed to fetch plans')
    const data = await response.json()
    return data.data || []
}

export function PricingSection() {
    const { t } = useTranslation('landing')
    const [searchParams] = useSearchParams()

    const { data: plans, isLoading, isError } = useQuery({
        queryKey: ['public-plans'],
        queryFn: fetchPublicPlans,
    })

    // Get selected plan from URL if any
    const selectedPlanId = searchParams.get('plan_id')

    if (isLoading) {
        return (
            <section className="py-20 bg-background">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            {t('pricing.title', 'Choose Your Plan')}
                        </h2>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            {t('pricing.subtitle', 'Select the perfect plan for your needs')}
                        </p>
                    </div>
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                </div>
            </section>
        )
    }

    if (isError) {
        return (
            <section className="py-20 bg-background">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            {t('pricing.title', 'Choose Your Plan')}
                        </h2>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            {t('pricing.subtitle', 'Select the perfect plan for your needs')}
                        </p>
                    </div>
                    <div className="flex justify-center items-center py-20">
                        <div className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="w-5 h-5" />
                            <span>Unable to load pricing plans. Please try again later.</span>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    if (!plans || plans.length === 0) {
        return null
    }

    return (
        <section id="pricing" className="py-20 bg-gradient-to-br from-emerald-500/10 via-purple-500/5 to-pink-500/10">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                        {t('pricing.title', 'Choose Your Plan')}
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        {t('pricing.subtitle', 'Select the perfect plan for your needs')}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {plans.map((plan) => {
                        const isSelected = selectedPlanId === plan.slug
                        const features = Object.entries(plan.features)

                        return (
                            <Card
                                key={plan.slug}
                                className={`
                                    relative border-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl
                                    ${plan.is_featured
                                        ? 'border-primary shadow-lg shadow-primary/20'
                                        : 'border-border hover:border-primary/50'
                                    }
                                    ${isSelected ? 'ring-4 ring-primary/30' : ''}
                                `}
                            >
                                {plan.is_featured && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <span className="bg-gradient-to-r from-emerald-500 to-purple-500 text-white text-xs font-medium px-4 py-1 rounded-full shadow-lg">
                                            Most Popular
                                        </span>
                                    </div>
                                )}

                                <CardHeader className="text-center pb-4">
                                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                                    <CardDescription className="mt-2">
                                        {plan.description}
                                    </CardDescription>
                                </CardHeader>

                                <CardContent className="space-y-6">
                                    {/* Price */}
                                    <div className="text-center">
                                        <div className="flex items-baseline justify-center gap-1">
                                            <span className="text-4xl font-bold">
                                                {plan.price_formatted}
                                            </span>
                                            <span className="text-muted-foreground">
                                                /{plan.interval}
                                            </span>
                                        </div>
                                        {plan.trial_days > 0 && (
                                            <p className="text-sm text-primary mt-2 font-medium">
                                                {plan.trial_days} day free trial
                                            </p>
                                        )}
                                    </div>

                                    {/* Features */}
                                    <ul className="space-y-3">
                                        {features.slice(0, 6).map(([key, feature]) => (
                                            <li key={key} className="flex items-start gap-3">
                                                {feature.enabled ? (
                                                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                                ) : (
                                                    <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                                )}
                                                <span className={feature.enabled ? 'text-foreground' : 'text-muted-foreground'}>
                                                    {feature.label}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA Button */}
                                    <Link to={`/register?plan_id=${plan.slug}`} className="block">
                                        <Button
                                            className={`w-full ${plan.is_featured
                                                    ? 'bg-gradient-to-r from-emerald-500 to-purple-500 hover:from-emerald-600 hover:to-purple-600 shadow-lg shadow-primary/25'
                                                    : ''
                                                }`}
                                            variant={plan.is_featured ? 'default' : 'outline'}
                                            size="lg"
                                        >
                                            Get Started
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>

                {/* Money-back guarantee note */}
                <p className="text-center text-sm text-muted-foreground mt-10">
                    {t('pricing.guarantee', '30-day money-back guarantee • Cancel anytime • No hidden fees')}
                </p>
            </div>
        </section>
    )
}